import { getElementTypes } from "core-integration/src/store/element-types/reducers";
import { IElementType } from "core-integration/src/store/element-types/types";
import { getElements } from "core-integration/src/store/elements/reducers";
import { IElement } from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { ITeam, ITeamsById } from "core-integration/src/store/teams/types";
import { integerToMoney } from "core-integration/src/utils/money";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { HelmetHead } from "../HelmetHead";

interface IElementListElementProps {
  element: IElement;
  team: ITeam;
}
const ElementListElement = ({ element, team }: IElementListElementProps) => {
  const { t } = useTranslation();
  return (
    <Row>
      <Cell>{element.web_name}</Cell>
      <Cell>{team.name}</Cell>
      <Cell>{element.total_points}</Cell>
      <Cell>
        {t("currencySymbol", "£{{elementValue}}", {
          elementValue: integerToMoney(element.now_cost, 10),
        })}
      </Cell>
    </Row>
  );
};

interface IElementListTableProps {
  elements: IElement[];
  teamsById: ITeamsById;
}
const ElementListTable = ({ elements, teamsById }: IElementListTableProps) => {
  const { t } = useTranslation();
  return (
    <Table aria-label={t("elementList", "Element List")}>
      <TableHeader>
        <Column isRowHeader>{t("player", "Player")}</Column>
        <Column>{t("team", "Team")}</Column>
        <Column>{t("points", "Points")}</Column>
        <Column>{t("cost", "Cost")}</Column>
      </TableHeader>
      <TableBody>
        {elements.map((element) => (
          <ElementListElement
            element={element}
            key={element.id}
            team={teamsById[element.team]}
          />
        ))}
      </TableBody>
    </Table>
  );
};

interface IElementListPositionProps {
  elements: IElement[];
  elementType: IElementType;
  teamsById: ITeamsById;
}
const ElementListPosition = ({
  elements,
  elementType,
  teamsById,
}: IElementListPositionProps) => {
  const halfElements = Math.ceil(elements.length / 2);
  const firstHalfElements = elements.slice(0, halfElements);
  const secondHalfElements = elements.slice(halfElements, elements.length);
  return (
    <SurfaceContainer>
      <Subheading>{elementType.plural_name}</Subheading>
      <div style={{ display: "flex" }}>
        <ElementListTable elements={firstHalfElements} teamsById={teamsById} />
        <ElementListTable elements={secondHalfElements} teamsById={teamsById} />
      </div>
    </SurfaceContainer>
  );
};

const PlayerList = () => {
  const { t } = useTranslation();
  const elements = useSelector(getElements);
  const elementTypes = useSelector(getElementTypes);
  const teamsById = useSelector(getTeamsById);

  return (
    <>
      <HelmetHead
        title={t(
          "playerList.metadata.title",
          "Full Player List 2026/27 | Fantasy Premier League",
        )}
        description={t(
          "playerList.metadata.description",
          "To view the Fantasy Premier League Player List for all players in season 2026/27, visit the official website of the Premier League.",
        )}
      />
      <div className="ism-main-full">
        {elementTypes.map((et) => (
          <ElementListPosition
            elements={elements
              .filter((e) => e.element_type === et.id)
              .sort((a, b) => b.now_cost - a.now_cost)}
            elementType={et}
            key={et.id}
            teamsById={teamsById}
          />
        ))}
      </div>
    </>
  );
};

export default PlayerList;
